/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect } from "react";

import zIndex from "../utils/zIndex";

interface AnchoredDivProps {
  /** Ref to anchor to */
  anchorRef: React.RefObject<HTMLElement>;
  /** Whether to show div */
  show?: boolean;
  /** When clicking away */
  onBlur?: () => void;
  /** Distance from anchor element */
  offset?: { x: number, y: number }
  /** Set this to true if you want your component to lose state when hiding */
  unmountOnBlur?: boolean;
}

const AnchoredDiv: React.FC<React.PropsWithChildren<AnchoredDivProps>> = ({
  anchorRef,
  show = false,
  onBlur,
  offset = { x: 0, y: 10 },
  children,
  unmountOnBlur = false,
}) => {
  const mountedRef = React.useRef(false);
  const innerRef = React.useRef<HTMLDivElement>(null);
  const isBubbledRef = React.useRef(false);

  // Ensure the innerRef is close to the anchorRef, but doesn't overlap left or right of screen.
  // On mobile, this will be a model style interface instead.
  const adjustPosition = () => {
    if (!innerRef.current || !anchorRef.current) {
      return;
    }

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const innerRect = innerRef.current.getBoundingClientRect();

    const x = anchorRect.left + offset.x;
    const y = anchorRect.top + anchorRect.height + offset.y;

    innerRef.current.style.left = `${Math.min(x, window.innerWidth - innerRect.width)}px`;
    innerRef.current.style.top = `${Math.min(y, window.innerHeight - innerRect.height)}px`;
  };

  // Click away for mobile
  useEffect(() => {
    const handleClickAway = (e: MouseEvent) => {
      if (!mountedRef.current) return;
      if (isBubbledRef.current) {
        isBubbledRef.current = false;
        return;
      }
      if (!innerRef.current?.contains(e.target as Node)) {
        onBlur?.();
      }
    };

    if (!show) {
      return () => undefined;
    }

    document.addEventListener("click", handleClickAway);
    return () => {
      document.removeEventListener("click", handleClickAway);
    };
  }, [show, onBlur]);

  useEffect(() => {
    // Ensures the clickAway doesn't fire on open click.
    setTimeout(() => { mountedRef.current = show; }, 0);

    return () => {
      mountedRef.current = false;
    };
  }, [show]);

  useEffect(() => {
    adjustPosition();

    window.addEventListener("resize", adjustPosition);
    return () => {
      window.removeEventListener("resize", adjustPosition);
    };
  }, [anchorRef, offset]);

  if (!anchorRef.current) {
    return null;
  }

  return (
    <>
      {/* Desktop display logic */}
      <div
        className="hidden sm:block absolute"
        ref={innerRef}
        style={{
          zIndex: zIndex.anchoredDiv,
          transition: "visibility 0.1s, opacity 0.1s",
          visibility: show ? "visible" : "hidden",
          opacity: show ? 1 : 0,
        }}
        onClick={() => {
          isBubbledRef.current = true;
        }}
      >
        {(!unmountOnBlur || show) && children}
      </div>

      {/* Mobile display logic */}
      <div
        className="flex sm:hidden absolute p-4 top-0 left-0 w-full h-full bg-overlay-50 justify-center items-center"
        onClick={() => {
          onBlur?.();
        }}
        style={{
          zIndex: zIndex.anchoredDiv,
          transition: "visibility 0.1s, opacity 0.1s",
          visibility: show ? "visible" : "hidden",
          opacity: show ? 1 : 0,
        }}
      >
        <div onClick={(e) => e.stopPropagation()}>
          {(!unmountOnBlur || show) && children}
        </div>
      </div>
    </>
  );
};

export default AnchoredDiv;
